import { DatePicker } from 'antd';
import moment, { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import i18n from '../../i18n/config';
import { IncidentType, IncidentTypeLabel, UserRole } from '../../models';
import { UserService } from '../../services';
import {
  UserActionStatistics,
  UserEquipmentStatistics,
  UserEventStatistics,
  UserRaCompletionTrend,
  UserRiskAssessmentStatistics,
  UserStatusTrend,
  userStatisticsService,
} from '../../services/user.statistics.service';
import { CHART_COLORS, COLORS, DASHBOARD_CHART_COLORS } from '../../shared';
import { HasSpinner } from '../../components';
import { HasChart, HasText, HasTitle } from '../../components/atoms';
import HasPieChart from '../../components/molecules/data-display/pie.chart';
import MyActivityTables from '../../components/organisms/user/my-activity-tables';
import UserMyAssignments from '../../components/organisms/user/user-my-assignments';
import { UserOutlined, WarningOutlined } from '@ant-design/icons';
import { formatUserRole } from '../../utils';
import './user-activity.scss';

interface UserStats {
  events: UserEventStatistics;
  actions: UserActionStatistics;
  equipment: UserEquipmentStatistics;
  riskAssessments: UserRiskAssessmentStatistics;
}

const buildEventChartData = (
  countByType: Partial<Record<IncidentType, number>>
): { labels: string[]; series: number[] } => {
  const labels: string[] = [];
  const series: number[] = [];
  Object.entries(countByType).forEach(([type, count]) => {
    const key = IncidentTypeLabel[type as IncidentType];
    labels.push(key ? i18n.t(key) : type);
    series.push(count!);
  });
  return { labels, series };
};

const HasUserStatisticsPage = () => {
  const [userId, setUserId] = useState<number | null>(null);
  const [loggedInUser, setLoggedInUser] = useState<{
    fullName: string;
    role: string;
    division: string;
    company: string;
  } | null>(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [activeTab, setActiveTab] = useState('actions');
  const [myWorkload, setMyWorkload] = useState<UserStatusTrend | null>(null);
  const [raCompletionTrend, setRaCompletionTrend] = useState<UserRaCompletionTrend | null>(null);
  const [workloadYear, setWorkloadYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    if (activeTab !== 'riskAssessments') {
      userStatisticsService.getEarliestOpenYear(activeTab).then((res) => setWorkloadYear(res.data));
    }
  }, [activeTab]);

  useEffect(() => {
    UserService.getMyUser()
      .then(({ data: user }) => {
        setUserId(user.id);
        setLoggedInUser({
          fullName: user.fullName,
          role: user.role,
          division: user.division.name,
          company: user.company.name,
        });
        setLoadingUser(false);
      })
      .catch(() => setLoadingUser(false));

    setLoadingStats(true);
    Promise.allSettled([
      userStatisticsService.getEventStatistics(),
      userStatisticsService.getActionStatistics(),
      userStatisticsService.getEquipmentStatistics(),
      userStatisticsService.getRiskAssessmentStatistics(),
    ]).then(([eventsRes, actionsRes, equipmentRes, raRes]) => {
      setStats({
        events: eventsRes.status === 'fulfilled' ? eventsRes.value.data : null,
        actions: actionsRes.status === 'fulfilled' ? actionsRes.value.data : null,
        equipment: equipmentRes.status === 'fulfilled' ? equipmentRes.value.data : null,
        riskAssessments: raRes.status === 'fulfilled' ? raRes.value.data : null,
      } as UserStats);
      setLoadingStats(false);
    });
  }, []);

  useEffect(() => {
    if (activeTab === 'riskAssessments') {
      if (!raCompletionTrend) {
        userStatisticsService.getRaCompletionTrend().then((res) => setRaCompletionTrend(res.data));
      }
    } else {
      userStatisticsService.getMyWorkload(activeTab, workloadYear).then((res) => setMyWorkload(res.data));
    }
  }, [activeTab, workloadYear]);

  const getActiveTabTitle = () => {
    switch (activeTab) {
      case 'inspections':
        return i18n.t('inspection.inspections', 'Inspections');
      case 'maintenances':
        return i18n.t('maintenance.maintenances', 'Maintenances');
      case 'riskAssessments':
        return i18n.t('riskAssessment.riskAssessments', 'Risk Assessments');
      default:
        return i18n.t('shared.actions', 'Actions');
    }
  };

  const toMonthLabels = (months: string[]) =>
    months.map((m) => {
      const [year, month] = m.split('-');
      return new Date(Number(year), Number(month) - 1).toLocaleString('default', { month: 'short' });
    });

  const baseChartOptions = (): any => ({
    chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'Montserrat' },
    plotOptions: { bar: { horizontal: false, columnWidth: '70%', borderRadius: 2 } },
    dataLabels: { enabled: false },
    yaxis: {
      labels: {
        style: { fontSize: '11px', colors: COLORS.GRAY_ACCENT_DARK },
        formatter: (v: number) => Math.round(v).toString(),
      },
    },
    legend: { show: false },
    tooltip: { shared: true, intersect: false },
    grid: { borderColor: COLORS.GRAY_ACCENT_LIGHT },
  });

  const renderMyWorkload = () => {
    if (activeTab === 'riskAssessments') {
      if (!raCompletionTrend) return null;
      const options = {
        ...baseChartOptions(),
        title: {
          text: `${i18n.t('riskAssessment.riskAssessments', 'Risk Assessments').toUpperCase()} - ${i18n.t('user.statistics.completionTrend', 'COMPLETION TREND')}`,
          floating: true,
          margin: 10,
          offsetY: -5,
          style: { fontSize: '14px', fontWeight: '700', fontFamily: 'Montserrat', color: COLORS.NEW_ORANGE },
        },
        subtitle: {
          text: i18n.t('dataDisplay.last12Months', 'Last 12 months'),
          align: 'right',
          floating: true,
          offsetY: 0,
          style: { fontSize: '12px', fontWeight: 'normal', fontFamily: 'Montserrat', color: COLORS.GRAY_ACCENT_DARK },
        },
        colors: [COLORS.GREEN],
        xaxis: {
          categories: toMonthLabels(raCompletionTrend.months),
          labels: { style: { fontSize: '11px', colors: COLORS.GRAY_ACCENT_DARK } },
        },
      };
      const series = [{ name: i18n.t('shared.completed', 'Completed'), data: raCompletionTrend.completed }];
      return (
        <div style={{ flex: 1, minHeight: 0 }}>
          <HasChart options={options} series={series} type="bar" height={'100%'} />
        </div>
      );
    }
    if (!myWorkload) return null;
    const options = {
      ...baseChartOptions(),
      colors: DASHBOARD_CHART_COLORS.slice(0, 2),
      xaxis: {
        categories: toMonthLabels(myWorkload.months),
        labels: { style: { fontSize: '11px', colors: COLORS.GRAY_ACCENT_DARK } },
      },
    };
    const series = [
      { name: i18n.t('shared.open', 'Open'), data: myWorkload.open },
      { name: i18n.t('action.overdue', 'Overdue'), data: myWorkload.overdue },
    ];
    return (
      <>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <h3 className="ant-descriptions-title" style={{ marginLeft: 9, marginTop: 3 }}>
            {`${getActiveTabTitle().toUpperCase()} - ${i18n.t('user.statistics.myWorkload', 'MY WORKLOAD')}`}
          </h3>
          <DatePicker
            picker="year"
            value={moment().year(workloadYear)}
            onChange={(m: Moment | null) => {
              if (m) setWorkloadYear(m.year());
            }}
            allowClear={false}
            style={{ width: 100 }}
          />
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <HasChart options={options} series={series} type="bar" height={'100%'} />
        </div>
      </>
    );
  };

  const renderMyReportsDonuts = () => {
    if (!stats) return null;
    const { events } = stats;
    const { labels: eventLabels, series: eventSeries } = buildEventChartData(events.countByType);

    const reportedByMeEventsOptions: any = {
      chart: { type: 'donut', toolbar: { show: false } },
      labels: eventLabels,
      colors: CHART_COLORS,
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              total: {
                show: true,
                label: i18n.t('shared.total', 'Total'),
                fontSize: '14px',
                fontFamily: 'Montserrat',
                color: COLORS.GRAY_ACCENT_DARK,
                formatter: () => String(events.total),
              },
              value: { fontSize: '22px', fontWeight: 700, fontFamily: 'Montserrat', color: COLORS.GRAY_DARK },
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '12px',
        fontFamily: 'Montserrat',
        formatter: (name: string, opts: any) => `${name}  <b>${opts.w.globals.series[opts.seriesIndex]}</b>`,
      },
      dataLabels: { enabled: false },
      tooltip: { enabled: true },
    };

    const reportedLabels = [
      i18n.t('shared.actions', 'Actions'),
      i18n.t('inspection.inspections', 'Inspections'),
      i18n.t('maintenance.maintenances', 'Maintenances'),
      i18n.t('riskAssessment.riskAssessments', 'Risk Assessments'),
    ];
    const reportedSeries = [
      stats.actions.reportedActions,
      stats.equipment.inspection.reported,
      stats.equipment.maintenance.reported,
      stats.riskAssessments.reported,
    ];
    const reportedTotal = reportedSeries.reduce((s, v) => s + v, 0);

    const reportedByMeWorkOptions: any = {
      chart: { type: 'donut', toolbar: { show: false } },
      labels: reportedLabels,
      colors: DASHBOARD_CHART_COLORS,
      plotOptions: {
        pie: {
          donut: {
            size: '72%',
            labels: {
              show: true,
              total: {
                show: true,
                label: i18n.t('shared.total', 'Total'),
                fontSize: '14px',
                fontFamily: 'Montserrat',
                color: COLORS.GRAY_ACCENT_DARK,
                formatter: () => String(reportedTotal),
              },
              value: { fontSize: '22px', fontWeight: 700, fontFamily: 'Montserrat', color: COLORS.GRAY_DARK },
            },
          },
        },
      },
      legend: {
        show: true,
        position: 'bottom',
        fontSize: '12px',
        fontFamily: 'Montserrat',
        formatter: (name: string, opts: any) => `${name}  <b>${opts.w.globals.series[opts.seriesIndex]}</b>`,
      },
      dataLabels: { enabled: false },
      tooltip: { enabled: true },
    };

    return (
      <>
        <h3 className={'ant-descriptions-title'} style={{ marginLeft: 9, marginTop: 3 }}>
          {i18n.t('user.statistics.myReports', 'MY REPORTS')}
        </h3>
        {}
        <div className="d-flex flex-column flex-grow-1" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <HasChart options={reportedByMeEventsOptions} series={eventSeries} type="donut" height={220} />
          <HasChart options={reportedByMeWorkOptions} series={reportedSeries} type="donut" height={220} />
        </div>
      </>
    );
  };

  const renderStatusPieChart = () => {
    if (!stats) return null;

    const title = getActiveTabTitle();
    let open = 0,
      overdue = 0,
      closed = 0;

    switch (activeTab) {
      case 'inspections':
        open = stats.equipment.inspection.open;
        overdue = stats.equipment.inspection.overdue;
        closed = stats.equipment.inspection.closed;
        break;
      case 'maintenances':
        open = stats.equipment.maintenance.open;
        overdue = stats.equipment.maintenance.overdue;
        closed = stats.equipment.maintenance.closed;
        break;
      case 'riskAssessments':
        open = stats.riskAssessments.open;
        overdue = stats.riskAssessments.overdue;
        closed = stats.riskAssessments.closed;
        break;
      default:
        open = stats.actions.openActions;
        overdue = stats.actions.overdueActions;
        closed = stats.actions.closedActions;
    }

    const labels = [
      i18n.t('shared.open', 'Open'),
      i18n.t('action.overdue', 'Overdue'),
      i18n.t('shared.closed', 'Closed'),
    ];
    const closedWithoutForm = activeTab === 'actions' ? (stats.actions.closedWithoutComplianceForm ?? 0) : 0;

    return (
      <div style={{ flex: 1, minHeight: 0 }}>
        <HasPieChart
          title={`${title.toUpperCase()} - ${i18n.t('user.statistics.status', 'STATUS')}`}
          series={[open, overdue, closed]}
          labels={labels}
          colors={DASHBOARD_CHART_COLORS.slice(0, 3)}
        />
        {closedWithoutForm > 0 && (
          <div className="d-flex align-items-center">
            <WarningOutlined className="align-middle mr-2" style={{ color: COLORS.GRAY_DARK, fontSize: 14 }} />
            <HasText
              content={`${closedWithoutForm} ${i18n.t('action.closedWithoutComplianceForm', 'closed action(s) missing compliance form')}`}
              style={{ color: COLORS.GRAY_ACCENT_DARK, fontSize: 12 }}
            />
          </div>
        )}
      </div>
    );
  };

  if (loadingUser) return <HasSpinner size="large" />;

  return loadingStats ? (
    <HasSpinner size="large" />
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto' }}>
      {loggedInUser && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            backgroundColor: COLORS.SIDE_BAR,
            padding: 10,
            gap: 10,
            flexShrink: 0,
            marginBottom: 5,
          }}
        >
          <UserOutlined style={{ verticalAlign: 'middle', fontSize: '30px', color: COLORS.NEW_ORANGE }} />
          <div className="d-flex flex-column">
            <HasTitle content={loggedInUser.fullName} level={4} style={{ color: COLORS.WHITE, marginBottom: 0 }} />
            <span style={{ color: COLORS.GRAY_ACCENT_LIGHT }}>
              {[formatUserRole(loggedInUser.role as UserRole), loggedInUser.company, loggedInUser.division]
                .filter(Boolean)
                .join(' · ')}
            </span>
          </div>
        </div>
      )}
      <div className="user-stats-grid">
        <div className="user-stats-allTimeStatus">
          <div className="flex-grow-1 h-100 d-flex flex-column has-container-tile">{renderStatusPieChart()}</div>
        </div>
        <div className="user-stats-statusTrend">
          <div className="flex-grow-1 h-100 d-flex flex-column has-container-tile">{renderMyWorkload()}</div>
        </div>
        <div className="user-stats-myWork">
          {userId != null && <MyActivityTables userId={userId} onTabChange={setActiveTab} />}
        </div>
        <div className="user-stats-reportedByMe">
          <div className="h-100 d-flex flex-column has-container-tile w-100">{renderMyReportsDonuts()}</div>
        </div>
        <div className="user-stats-workloadTrend">
          <UserMyAssignments activeTab={activeTab} />
        </div>
      </div>
    </div>
  );
};

export default HasUserStatisticsPage;
